import { GMI, STATE } from "../../../constants";
import services from "../../../services";
import { types } from "../../../utils/js/types";
import { batchActions } from "../../../utils/redux";
import request from "../../request";

const { TRIPS } = STATE.UI;

/**
 * @typedef {"last_name" | "driver_license"} FieldNames
 */

/** @type {Record<FieldNames, string[]>} */
const FIELD_PATHS = {
  last_name: TRIPS.PAST_UNAUTH.LAST_NAME,
  driver_license: TRIPS.PAST_UNAUTH.DRIVER_LICENSE
};

/**
 * @description Clears a field inside gmi.ui.trips.past.unauth[fieldName]
 * @private
 * @param {FieldNames} field
 * @returns {Object} {type: string, path: [], value: null}
 */
function clearField(field) {
  const path = FIELD_PATHS[field];

  return {
    type: GMI.SET_GMI_STATE,
    path,
    value: null
  };
}

/**
 * @description Unsetter for the gmi.ui.trips.past.unauth[fieldName]
 * @private
 * @returns {Object} Unsetter action object
 */
function clearState() {
  const batchedActions = [];

  Object.keys(FIELD_PATHS).forEach(field => {
    batchedActions.push(clearField(field));
  });

  return batchActions(...batchedActions);
}

/**
 * @description Sets a field inside gmi.ui.trips.past.unauth[fieldName] state
 * @private
 * @param {FieldNames} field
 * @param {unknown} value
 * @returns {Object} {type: string, path: [], value: object}
 */
function setField(field, value) {
  const path = FIELD_PATHS[field];

  return {
    type: GMI.SET_GMI_STATE,
    path,
    value
  };
}

/**
 * @description Setter for the gmi.ui.trips.past.unauth[fieldName] data
 * @private
 * @param {Record<FieldNames, unknown>} fields - Key-value pairs of fields and values to set
 * @returns {Object} Batched action that sets each passed field
 */
const setState = (fields = {}) => {
  const batchedActions = [];

  Object.entries(fields).forEach(([key, value]) => {
    if (FIELD_PATHS[key] && value) {
      batchedActions.push(setField(key, value));
    }
  });

  return batchActions(...batchedActions);
};

export const state = {
  /**
   * @name set
   * @description A global setter for gmi.ui.trips.past.unauth[fieldName] data
   * @param {FieldNames | null} [field]
   * @param {Object} [args] - Value to set
   * @returns {Function} Dispatch function
   * @memberof actions.trips.past.unauth
   * @example
   *  dispatch(actions.trips.past.unauth.state.set('last_name', 'dummy'));
   *  dispatch(actions.trips.past.unauth.state.set('driver_license', 'hauhau123'));
   *
   */
  set(field = null, value = {}) {
    return (dispatch) => {
      if (!types(field).isString) return dispatch(setState(value));
      if (FIELD_PATHS[field]) return dispatch(setField(field, value));
    };
  },

  /**
   * @name unset
   * @description A global unsetter for gmi.ui.trips.past.unauth[fieldName] data
   * @param {FieldNames | null} [field]
   * @returns {Function} Dispatch function
   * @memberof actions.trips.past.unauth
   * @example 
   * store.dispatch(actions.trips.past.unauth.state.unset('last_name'));
   *
   */
  unset(field = null) {
    return (dispatch) => {
      if (!types(field).isString) return dispatch(clearState());
      if (FIELD_PATHS[field]) return dispatch(clearField(field));
    };
  }
};

/**
 * @description Calls unauth past rental GMA endpoint
 * @param {Object} object
 * @example store.dispatch(actions.trips.past.unauth.getUnauthPastRental());
 * @returns {Function} Dispatched service action
 */
export function getUnauthPastRental() {
  return (dispatch, getState) => {
    const state = getState();

    const payload = state
      .getIn(TRIPS.PAST_UNAUTH.PATH)
      ?.toJS();

    const data = {
      payload
    };

    return dispatch(
      request({
        service: () => services.gma.past.unauth(data)
      })
    );
  };
}

export default {
  getUnauthPastRental,
  state
};